import React from 'react';
import { BookOpen, KeyRound, Library, Users } from 'lucide-react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useOverview, useSummaryOptions } from '../hooks/useActivationReport';
import { reportStateProps } from '../hooks/useReportList';
import { activationReportService } from '../services/activationReportService';
import { GroupBy, ReportFilters } from '../types';
import { exportFileName, exportFilters, overviewWorkbook } from '../utils/exportReport';
import { formatPeriod } from '../utils/period';
import ExportButton from './ExportButton';
import KpiCard from './KpiCard';
import ReportState from './ReportState';

interface OverviewTabProps {
  filters: ReportFilters;
  onGroupByChange: (g: GroupBy) => void;
}

const GROUP_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: 'day', label: 'Ngày' },
  { value: 'week', label: 'Tuần' },
  { value: 'month', label: 'Tháng' },
];

const OverviewTab: React.FC<OverviewTabProps> = ({ filters, onGroupByChange }) => {
  const query = useOverview(filters);
  const { data, refetch } = query;
  const groupBy = filters.group_by ?? 'day';
  const summaryOptions = useSummaryOptions(filters.school_id);

  const handleExport = async () => {
    const overview = await activationReportService.overview(exportFilters(filters));
    return { workbook: overviewWorkbook(overview, filters, summaryOptions) };
  };

  const inventory = data
    ? [
        { label: 'Tổng mã', value: data.inventory.total },
        { label: 'Đã dùng', value: data.inventory.used },
        { label: 'Còn lại', value: data.inventory.unused },
        { label: 'Đã thu hồi', value: data.inventory.revoked },
        { label: 'Hết hạn', value: data.inventory.expired },
      ]
    : [];

  // Kho mã không theo ngày nên không tính vào "rỗng"; chỉ biểu đồ được thay bằng thông báo
  const isEmpty = !!data && data.kpis.activations === 0 && data.series.every((s) => s.count === 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <ExportButton onExport={handleExport} fileName={exportFileName('tong-quan', filters)} />
      </div>

      <ReportState {...reportStateProps(query)}>
        {data && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
              <KpiCard title="Lượt kích hoạt" value={data.kpis.activations} icon={KeyRound} />
              <KpiCard title="Người đã kích hoạt" value={data.kpis.activated_users} icon={Users} />
              <KpiCard title="Sách đã kích hoạt" value={data.kpis.activated_books} icon={BookOpen} />
              <KpiCard title="Bộ sách đã kích hoạt" value={data.kpis.activated_collections} icon={Library} />
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Kho mã</h3>
                <span className="text-xs text-gray-500">Tình trạng hiện tại, không theo ngày/trường</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {inventory.map((item) => (
                  <div key={item.label} className="rounded-lg bg-gray-50 p-4">
                    <p className="text-sm text-gray-600">{item.label}</p>
                    <p className="text-xl font-bold text-gray-900 mt-1">{item.value.toLocaleString('vi-VN')}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Lượt kích hoạt theo thời gian</h3>
                <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                  {GROUP_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => onGroupByChange(opt.value)}
                      aria-pressed={groupBy === opt.value}
                      className={`px-3 py-1.5 text-sm font-medium ${
                        groupBy === opt.value ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <ReportState isLoading={false} isError={false} isEmpty={isEmpty} onRetry={() => refetch()}>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={data.series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" tickFormatter={(p: string) => formatPeriod(p, groupBy)} />
                    <YAxis allowDecimals={false} />
                    <Tooltip labelFormatter={(p) => formatPeriod(String(p), groupBy)} />
                    <Line type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={2} name="Lượt kích hoạt" />
                  </LineChart>
                </ResponsiveContainer>
              </ReportState>
            </div>
          </div>
        )}
      </ReportState>
    </div>
  );
};

export default OverviewTab;
